import { ChevronRight, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { cn } from '@/lib/utils';
import type { ProviderDirectoryV2Service } from '@/services/types/provider-directory-v2-types';

interface RelatedProvidersProps {
  services: ProviderDirectoryV2Service[];
  /** The current service ID to exclude from the list */
  currentServiceId: string;
  /** Organization name for the sub-header */
  organizationName?: string;
  className?: string;
}

/** Derive fax status label for display. */
const getFaxLabel = (service: ProviderDirectoryV2Service): string => {
  const contacts = service.contact_points ?? [];
  const faxContacts = contacts.filter(
    (c) => c.point_type === 'FAX' && c.value,
  );
  if (faxContacts.length === 0) return 'No fax';
  if (
    faxContacts.some(
      (c) => c.verification_status === 'VERIFIED' || Boolean(c.verified_at),
    )
  )
    return 'Fax verified';
  return 'Fax unverified';
};

/** Derive dot color: green if any contact verified, gray otherwise. */
const getDotColor = (service: ProviderDirectoryV2Service): string => {
  const contacts = service.contact_points ?? [];
  if (
    contacts.some(
      (c) => c.verification_status === 'VERIFIED' || Boolean(c.verified_at),
    )
  )
    return 'bg-success';
  return 'bg-zinc-400';
};

export const RelatedProviders = ({
  services,
  currentServiceId,
  organizationName,
  className,
}: RelatedProvidersProps) => {
  const navigate = useNavigate();

  const siblings = services.filter((s) => s.id !== currentServiceId);

  if (siblings.length === 0) return null;

  return (
    <div className={cn('', className)}>
      {/* Section title */}
      <h2 className="text-[13px] font-semibold uppercase tracking-[0.05em] text-zinc-500 mb-3">
        Related Providers
      </h2>

      {/* Container card */}
      <div className="rounded-xl border border-border bg-background overflow-clip">
        {/* Sub-header: Other services at [org name] */}
        {organizationName && (
          <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-3">
            <Home size={14} className="shrink-0 text-info-light" />
            <span className="text-[13px] font-medium text-zinc-600">
              Other services at
            </span>
            <span className="text-[13px] font-semibold text-primary">
              {organizationName}
            </span>
          </div>
        )}

        {/* Rows */}
        {siblings.map((service, idx) => {
          const faxLabel = getFaxLabel(service);
          const dotColor = getDotColor(service);

          return (
            <button
              key={service.id}
              type="button"
              onClick={() => navigate(`/provider-directory/${service.id}`)}
              className={cn(
                'flex w-full items-center gap-3 px-5 py-3 text-left transition-colors',
                'hover:bg-background-gray cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                idx < siblings.length - 1 &&
                  'border-b border-zinc-100',
              )}
            >
              {/* Verification dot */}
              <span
                className={cn(
                  'h-2 w-2 shrink-0 rounded-full',
                  dotColor,
                )}
              />

              {/* Name */}
              <span className="flex-1 min-w-0 truncate text-sm font-medium text-primary">
                {service.name}
              </span>

              {/* Fax status */}
              <span className="shrink-0 text-xs text-zinc-500">
                {faxLabel}
              </span>

              {/* Chevron */}
              <ChevronRight size={14} className="shrink-0 text-zinc-400" />
            </button>
          );
        })}
      </div>
    </div>
  );
};
